import React, { useState } from 'react';
import { CONTACT, NAV, SOCIALS } from './content';
import { Brand, CtaButton } from './parts';
import { reopenCookieBanner } from '../components/CookieBanner';
import TermsOfUse from '../components/TermsOfUse';

// Brand marks (Simple Icons, CC0), keyed by SOCIALS label.
const SOCIAL_ICONS = {
  LinkedIn:
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  X: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z',
  YouTube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
};

export default function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);
  const isHome = window.location.pathname === '/';
  const href = (h) => (isHome ? h : `/${h}`);

  return (
    <footer className="px-5 sm:px-8 pt-14 sm:pt-16 pb-8 border-t border-ink/10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-10 lg:gap-12">
          <div className="col-span-2 lg:col-span-5">
            <Brand size="lg" />
            <p className="mt-5 text-[1rem] leading-relaxed text-ink-soft max-w-sm">
              AI consulting for growing businesses. We map how the work really gets done before we
              automate any of it.
            </p>
            <div className="mt-7">
              <CtaButton small />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rb-label mb-4">Explore</div>
            <ul className="space-y-1 md:space-y-2.5 text-[0.96rem]">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={href(n.href)} className="rb-tap text-left text-ink-soft hover:text-brand transition-colors">
                    {n.label}
                  </a>
                </li>
              ))}
              <li>
                <a href="/privacy-policy" className="rb-tap text-left text-ink-soft hover:text-brand transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <button type="button" onClick={() => setTermsOpen(true)} className="rb-tap text-left text-ink-soft hover:text-brand transition-colors">
                  Terms of Use
                </button>
              </li>
              <li>
                <button type="button" onClick={reopenCookieBanner} className="rb-tap text-left text-ink-soft hover:text-brand transition-colors">
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="rb-label mb-4">Contact</div>
            <ul className="space-y-1 md:space-y-2.5 text-[0.96rem] text-ink-soft break-words">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="rb-tap hover:text-brand transition-colors">
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a href={CONTACT.phoneHref} className="rb-tap hover:text-brand transition-colors">
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer" className="rb-tap hover:text-brand transition-colors">
                  {CONTACT.addressLines[0]}
                  <br />
                  {CONTACT.addressLines[1]}
                </a>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-2 md:gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Primeslink on ${s.label}`}
                  title={s.label}
                  className="w-11 h-11 md:w-10 md:h-10 grid place-items-center rounded-full border border-ink/15 bg-white text-ink hover:text-white hover:bg-brand hover:border-brand transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={SOCIAL_ICONS[s.label]} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-ink/10 text-center text-[0.875rem] text-ink-mute">
          © {new Date().getFullYear()} Primeslink. All rights reserved.
        </div>
      </div>

      <div className="rb-legal">
        <TermsOfUse isOpen={termsOpen} onClose={() => setTermsOpen(false)} />
      </div>
    </footer>
  );
}
